import { Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Button, FormField, Typography, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import SystemSettingService from "../../services/message/SystemSetting.service";

import { ICodename, IInput } from "../../commons/interfaces";
import { Mode } from "../../constants/enums";

interface IDataSettingCode {
    id?: string;
    tenantCode?: string;
    serviceCode?: string;
    organizationId?: string;

    settingCode?: string;
    settingValue?: string;

    updateTime?: any;
    status?: number;
}

const SettingConfigFirebaseContainer = (props: {
    mode?: Mode;
    settingCode: string;
    listServiceCode?: ICodename[];
    hidenInputServiceCode?: boolean;

    onGoBack: () => void;
    onChangeMode: (mode: Mode) => void;
}) => {
    const { userInfo, serviceCode, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();

    const Strings = useStrings();
    const newMode = useMemo(() => props.mode || Mode.View, [props.mode]);

    const [dataSetting, setDataSetting] = useState<IDataSettingCode>({});
    const [valueServiceCode, setValueServiceCode] = useState<IInput>({});
    const [dataSettingValueFirebase, setDataSettingValueFirebase] = useState<{
        vapidKey?: IInput;
        adminSDK?: IInput;
        clientConfig?: IInput;
    }>({});

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props.settingCode)) {
            getDetailBySettingCode(props.settingCode);
        }
    }, [props.settingCode, userInfo?.userProfile?.organizationId]);

    const getDetailBySettingCode = async (valCode?: string) => {
        try {
            onShowLoading();

            const result = await SystemSettingService.getBySettingCode(valCode);

            const newVal = Helpers.isNullOrEmpty(result?.serviceCode) ? serviceCode : result?.serviceCode;
            setValueServiceCode({ value: newVal });

            if (!Helpers.isNullOrEmpty(result?.settingValue)) {
                const temp = JSON.parse(`${result?.settingValue}`.replace(/(\r\n|\n|\r)/gm, ""));

                setDataSettingValueFirebase({
                    vapidKey: { value: temp.vapidKey },
                    adminSDK: {
                        value: JSON.stringify(temp.adminSDK)
                            .replaceAll("{", "{\n\t")
                            .replaceAll("}", "\n}")
                            .replaceAll(",", ",\n\t")
                            .replaceAll(";", ";\n\t"),
                    },
                    clientConfig: {
                        value: JSON.stringify(temp.clientConfig)
                            .replaceAll("{", "{\n\t")
                            .replaceAll("}", "\n}")
                            .replaceAll(",", ",\n\t")
                            .replaceAll(";", ";\n\t"),
                    },
                });
            }

            setDataSetting({
                id: result.id,
                settingCode: result.settingCode,
                settingValue: result.settingValue,
                serviceCode: result.serviceCode,
                organizationId: result.organizationId,
                updateTime: result.updateTime,
                status: result.status,
            });
        } catch (error: any) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const onChangeValue = async (key: string, newValue: any) => {
        let dataTemp: any = { ...dataSettingValueFirebase };
        dataTemp[key] = { value: newValue };
        setDataSettingValueFirebase(dataTemp);
    };

    const handleValueObj = (value: any) => {
        let valTemp;
        const index1 = value.indexOf("{");
        const index2 = value.indexOf("}");

        if (index1 !== -1 && index2 !== -1) {
            valTemp = value.substring(index1, index2 + 1);
        } else {
            valTemp = (index1 !== -1 ? "" : "{") + value + (index2 !== -1 ? "" : "}");
        }

        return JSON.parse(`${valTemp}`.replace(/(\r\n|\n|\r)/gm, "").trim());
    };

    const onCheckValidateDataSetting = () => {
        let checked: boolean = true;
        let dataTemp: any = { ...dataSettingValueFirebase };

        const listKey = ["vapidKey", "adminSDK", "clientConfig"];
        listKey.forEach((key) => {
            if (Helpers.isNullOrEmpty(dataTemp[key]?.value)) {
                dataTemp[key] = { error: Strings.Common.REQUIRED };
                checked = false;
            }
            if (!Helpers.isNullOrEmpty(dataTemp[key]?.error)) {
                checked = false;
            }
        });

        if (!checked) {
            setDataSettingValueFirebase(dataTemp);
        }

        return checked;
    };

    const handleUpdateDataSetting = async () => {
        if (newMode === Mode.View) {
            props.onChangeMode(Mode.Update);
        } else {
            const isValidate = onCheckValidateDataSetting();
            if (!isValidate) {
                onError(Strings.Common.PLEASE_COMPLETE_ALL_INFORMATION);
                return;
            } else {
                if (Helpers.isNullOrEmpty(valueServiceCode?.value)) {
                    onError(Strings.Common.REQUIRED_SERVICE_CODE);
                    return;
                } else {
                    try {
                        onShowLoading();

                        let newSettingValue: any = {
                            vapidKey: dataSettingValueFirebase?.vapidKey?.value,
                            adminSDK: handleValueObj(dataSettingValueFirebase?.adminSDK?.value),
                            clientConfig: handleValueObj(dataSettingValueFirebase?.clientConfig?.value),
                        };

                        const orgId =
                            Helpers.isNullOrEmpty(dataSetting?.organizationId) || dataSetting?.organizationId === "0"
                                ? userInfo?.userProfile?.organizationId || "0"
                                : dataSetting?.organizationId;

                        const newDataSubmit = {
                            id: dataSetting?.id,
                            serviceCode: valueServiceCode?.value,
                            settingValue: JSON.stringify(newSettingValue),
                            settingCode: dataSetting?.settingCode,
                            organizationId: orgId,
                        };

                        let resultApi = await SystemSettingService.update(newDataSubmit);

                        if (resultApi.statusCode === 200) {
                            await getDetailBySettingCode(props.settingCode);

                            props.onChangeMode(Mode.View);

                            onSuccess(Strings.SETTING_FIREBASE.UPDATE_SUCCESS);
                        }
                    } catch (error) {
                        onHideLoading();
                        const e = Helpers.renderExceptionError(error);
                        onError(e);
                    } finally {
                        onHideLoading();
                    }
                }
            }
        }
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 2 }}>
                    <Button
                        color={"secondary"}
                        onClick={() => {
                            props.onGoBack();
                        }}
                    >
                        {Strings.Common.GO_BACK}
                    </Button>
                    <Button
                        sx={{ ml: 2 }}
                        color={"dark"}
                        onClick={() => {
                            handleUpdateDataSetting();
                        }}
                    >
                        {props.mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <Box p={2}>
                        <Box display="flex" flexWrap="wrap" alignItems="center">
                            <Typography variant="h5">{Strings.SETTING_FIREBASE.BASIC_INFO} </Typography>
                        </Box>
                        <Grid container spacing={3} p={2}>
                            {!props.hidenInputServiceCode && (
                                <Grid item xs={12}>
                                    <Autocomplete
                                        required
                                        key={valueServiceCode?.value}
                                        data={props.listServiceCode || []}
                                        disabled={props.mode === Mode.View}
                                        defaultValue={valueServiceCode?.value}
                                        errorMessage={valueServiceCode?.error}
                                        label={Strings.SETTING_FIREBASE.SERVICE_NAME}
                                        placeholder={Strings.SETTING_FIREBASE.SELECT_SERVICE}
                                        onChange={(val) => {
                                            setValueServiceCode({ value: val });
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <FormField
                                    required
                                    maxLength={255}
                                    disabled={newMode === Mode.View}
                                    label={Strings.SETTING_FIREBASE.VAPID_KEY}
                                    placeholder={Strings.SETTING_FIREBASE.ENTER_VAPID_KEY}
                                    errorMessage={dataSettingValueFirebase?.vapidKey?.error}
                                    defaultValue={dataSettingValueFirebase?.vapidKey?.value || ""}
                                    onBlur={(value) => {
                                        onChangeValue("vapidKey", value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    required
                                    multiline
                                    minRows={5}
                                    disabled={newMode === Mode.View}
                                    label={Strings.SETTING_FIREBASE.ADMIN_SDK}
                                    placeholder={Strings.SETTING_FIREBASE.ENTER_ADMIN_SDK}
                                    errorMessage={dataSettingValueFirebase?.adminSDK?.error}
                                    defaultValue={dataSettingValueFirebase?.adminSDK?.value || ""}
                                    onBlur={(value) => {
                                        onChangeValue("adminSDK", value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    required
                                    multiline
                                    minRows={5}
                                    disabled={newMode === Mode.View}
                                    label={Strings.SETTING_FIREBASE.CLIENT_CONFIG}
                                    placeholder={Strings.SETTING_FIREBASE.ENTER_CLIENT_CONFIG}
                                    errorMessage={dataSettingValueFirebase?.clientConfig?.error}
                                    defaultValue={dataSettingValueFirebase?.clientConfig?.value || ""}
                                    onBlur={(value) => {
                                        onChangeValue("clientConfig", value);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Grid>
        </Grid>
    );
};

export default SettingConfigFirebaseContainer;
